/* ==========================================================================
   OUTLETZONE — product-form.js
   <variant-selector> · <product-form> · <sticky-atc> · <fbt-bundle>

   Variant switching updates the URL, price, availability, SKU and stock line
   without a page load, but the page still works fully with JS disabled:
   the selectors are real radio inputs inside a real <form> that GETs the
   product URL with ?variant=.
   ========================================================================== */
(function () {
  'use strict';

  const OZ = window.OZ;
  const { $, $$, formatMoney, announce } = OZ.helpers;

  /* ====================================================== VARIANT SELECTOR */
  class VariantSelector extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.productUrl = this.dataset.url;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', () => this.onChange());
    }

    get selectedOptions() {
      return $$('fieldset', this).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant() {
      const selected = this.selectedOptions;
      return this.variants.find((v) =>
        v.options.every((opt, i) => selected[i] == null || opt === selected[i])
      );
    }

    onChange() {
      const variant = this.findVariant();

      // Reflect the chosen value next to each legend.
      $$('fieldset', this).forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        const out = fs.querySelector('[data-selected-value]');
        if (out && checked) out.textContent = checked.value;
      });

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateIdInput(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateMeta(variant);
      this.updateMedia(variant);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateUrl(variant) {
      if (!this.productUrl) return;
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }

    updateIdInput(variant) {
      const input = this.form?.querySelector('[data-variant-id]');
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updatePrice(variant) {
      const wrap = $('[data-price-wrapper]');
      if (!wrap) return;

      const current = wrap.querySelector('[data-price-current]');
      const compare = wrap.querySelector('[data-price-compare]');
      const badge = wrap.querySelector('[data-price-badge]');
      const unit = wrap.querySelector('[data-price-unit]');

      if (current) current.textContent = formatMoney(variant.price);

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      wrap.classList.toggle('price--on-sale', !!onSale);

      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }

      if (badge) {
        if (onSale) {
          const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          badge.textContent = `−${pct} %`;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }

      // Per-piece price on multipacks — reframes the number and defuses the
      // "is this expensive?" reflex before it forms.
      if (unit) {
        const pack = parseInt(unit.dataset.packCount || '1', 10);
        if (pack > 1) {
          unit.textContent = unit.dataset.template.replace('__PRICE__', formatMoney(variant.price / pack));
          unit.hidden = false;
        } else {
          unit.hidden = true;
        }
      }
    }

    updateButton(variant) {
      $$('[data-add-button]').forEach((btn) => {
        const label = btn.querySelector('[data-add-label]') || btn;
        if (!variant.available) {
          btn.disabled = true;
          label.textContent = OZ.strings.soldOut;
        } else {
          btn.disabled = false;
          label.textContent = OZ.strings.addToCart;
        }
      });
    }

    setUnavailable() {
      $$('[data-add-button]').forEach((btn) => {
        btn.disabled = true;
        const label = btn.querySelector('[data-add-label]') || btn;
        label.textContent = OZ.strings.unavailable;
      });
      const stock = $('[data-stock-indicator]');
      if (stock) stock.hidden = true;
    }

    updateMeta(variant) {
      const sku = $('[data-variant-sku]');
      if (sku) {
        sku.textContent = variant.sku || '';
        sku.hidden = !variant.sku;
      }

      const stock = $('[data-stock-indicator]');
      if (!stock) return;

      const threshold = OZ.settings.lowStockThreshold || 6;
      const qty = variant.inventory_quantity;
      const tracked = variant.inventory_management != null;

      stock.hidden = false;
      stock.classList.remove('stock--in', 'stock--low', 'stock--out');

      // Honest stock only. If Shopify is not tracking inventory we say
      // "in stock" and nothing more — we never invent a number.
      if (!variant.available) {
        stock.classList.add('stock--out');
        stock.querySelector('[data-stock-text]').textContent = stock.dataset.outText;
      } else if (tracked && qty > 0 && qty <= threshold) {
        stock.classList.add('stock--low');
        stock.querySelector('[data-stock-text]').textContent =
          stock.dataset.lowText.replace('__COUNT__', qty);
      } else {
        stock.classList.add('stock--in');
        stock.querySelector('[data-stock-text]').textContent = stock.dataset.inText;
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = $('product-gallery');
      if (gallery && gallery.goToMedia) gallery.goToMedia(variant.featured_media.id);
    }
  }
  customElements.define('variant-selector', VariantSelector);

  /* ========================================================== PRODUCT FORM */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[data-add-button]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      // "Acheter maintenant" adds to cart like the regular button, then skips
      // the cart page and goes straight to checkout — same destination as a
      // native Shopify buy-now, but a plain button we fully control the look of.
      const submitter = e.submitter;
      const buyNow = !!submitter?.hasAttribute('data-buy-now');

      if (buyNow) {
        let returnTo = this.form.querySelector('input[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          this.form.appendChild(returnTo);
        }
        returnTo.value = '/checkout';
      }

      // Cart drawer is an enhancement — with it off we let the form post normally,
      // and the return_to field above steers where the page lands.
      if (OZ.settings.cartType !== 'drawer') return;
      e.preventDefault();
      const btn = buyNow ? submitter : this.button;
      if (btn.disabled) return;

      btn.classList.add('btn--loading');
      btn.setAttribute('aria-disabled', 'true');
      if (this.errorEl) this.errorEl.hidden = true;

      const data = new FormData(this.form);
      const payload = {
        id: Number(data.get('id')),
        quantity: Number(data.get('quantity') || 1)
      };

      try {
        await OZ.cart.add(payload);
        if (buyNow) {
          window.location.href = '/checkout';
          return;
        }
        OZ.toast(OZ.strings.added);
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (err) {
        if (this.errorEl) {
          this.errorEl.textContent = err.message;
          this.errorEl.hidden = false;
        }
        announce(err.message);
      } finally {
        btn.classList.remove('btn--loading');
        btn.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ============================================================ STICKY ATC */
  // Appears only once the real buy button has scrolled out of view. Uses an
  // IntersectionObserver, so there is no scroll handler on the main thread.
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.watch);
      if (!target) return;

      document.body.classList.add('has-sticky-atc');

      const io = new IntersectionObserver(
        ([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { rootMargin: '0px 0px -100% 0px', threshold: 0 }
      );
      io.observe(target);

      this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
        target.closest('form')?.requestSubmit();
      });

      document.addEventListener('variant:change', (e) => {
        const price = this.querySelector('[data-sticky-price]');
        if (price) price.textContent = formatMoney(e.detail.variant.price);
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* =========================================================== FBT BUNDLE  */
  class FbtBundle extends HTMLElement {
    connectedCallback() {
      this.checkboxes = $$('input[type="checkbox"]', this);
      this.totalEl = this.querySelector('[data-fbt-total]');
      this.button = this.querySelector('[data-fbt-add]');

      this.addEventListener('change', () => this.updateTotal());
      this.button?.addEventListener('click', () => this.addAll());
      this.updateTotal();
    }

    get selected() {
      return this.checkboxes.filter((c) => c.checked);
    }

    updateTotal() {
      const total = this.selected.reduce((sum, c) => sum + parseInt(c.dataset.price, 10), 0);
      if (this.totalEl) this.totalEl.textContent = formatMoney(total);
      if (this.button) this.button.disabled = this.selected.length === 0;
    }

    async addAll() {
      const items = this.selected.map((c) => ({ id: Number(c.value), quantity: 1 }));
      if (!items.length) return;
      this.button.classList.add('btn--loading');
      try {
        await OZ.cart.add(items);
        OZ.toast(OZ.strings.added);
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (err) {
        OZ.toast(err.message || OZ.strings.cartError);
      } finally {
        this.button.classList.remove('btn--loading');
      }
    }
  }
  customElements.define('fbt-bundle', FbtBundle);

  /* ============================================================ QUICK ADD  */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('btn--loading');
    try {
      await OZ.cart.add({ id: Number(btn.dataset.quickAdd), quantity: 1 });
      OZ.toast(OZ.strings.added);
      document.dispatchEvent(new CustomEvent('cart:added'));
    } catch (err) {
      OZ.toast(err.message || OZ.strings.cartError);
    } finally {
      btn.classList.remove('btn--loading');
    }
  });

  /* ============================================================ SIZE GUIDE */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-modal-open]');
    if (open) {
      e.preventDefault();
      const modal = document.getElementById(open.dataset.modalOpen);
      if (!modal) return;
      modal.classList.add('is-open');
      document.body.classList.add('is-locked');
      modal._release = OZ.helpers.trapFocus(modal, modal.querySelector('[data-modal-close]'));
      modal._opener = open;
      return;
    }

    const close = e.target.closest('[data-modal-close]');
    const overlay = e.target.classList?.contains('modal__overlay');
    if (close || overlay) {
      const modal = e.target.closest('.modal');
      if (!modal) return;
      modal.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (modal._release) modal._release();
      if (modal._opener) modal._opener.focus();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const modal = document.querySelector('.modal.is-open');
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    if (modal._release) modal._release();
    if (modal._opener) modal._opener.focus();
  });
})();
